import { V2Error } from '../error';
import { V2_QUERY_TYPES } from './base';
import { ConditionalExpression, OPERATORS } from './condition';
import { Literal, Value } from './data-reference';
import { QueryExecutionResult, V2Query } from './queries';

/**
 * # Tracing
 *
 * ## Goals
 *
 * The goals of tracing are:
 *
 * 1. Provide high-level query performance measurements for monitoring purposes.
 * 2. Provide detailed profiling information to assess the performance of particular queries.
 * 3. Provide detailed logging to debug query evaluation (particularly conditional expressions).
 *
 * The above goals should be achieved without violating nonfunctional requirements:
 *
 * 1. The presence / abscence of tracing information should not affect existing SDK / API interfaces.
 * 2. Tracing should not become a significant burden on resources.
 *
 * # What's in a trace?
 *
 * As a query is executed, events occur which can leave a record in the trace.
 * Examples of events are 'Execute this query', or 'save this string to the trace log'.
 * For each of these events, it's important to know what happened, and exactly when it occurred.
 *
 * JS provides two timers: a millisecond resolution timestamp, and a much more precise
 * relative timer (typically with 5 microsecond resolution measured from page load / node process start).
 * Events in the trace log contain both timestamps.
 *
 * So traces are essentially collections of timestamped events.
 *
 * # Contexts
 *
 * It often makes sense to group events which belong to a common operation.
 * For example, tracing a conditional expression which 'OR's together two graphql queries could be confusing
 * if events occuring during the execution of the two graphql queries were mixed together.
 *
 * Instead, each query gets it's own context. Events which occur as a result of the query's execution are
 * recorded within the context. If the query's execution requires another query to be evaluated (for example
 * as a value passed into the graphql query), then the sub-query will get it's own context.
 *
 *
 */

type MaybeArray<T> = T | T[];

export enum TraceEventType {
  RootContext = 'rootContext',
  QueryContext = 'queryContext',
  TraceMessage = 'traceMessage',
}

export interface TraceTimestamp {
  abs: number; // absolute timestamp in milliseconds, generated with +(new Date())
  rel: number; // relative time in fractional milliseconds since page load, generated with performance.now()
}

export type ALL = '∀';

export interface TraceOptions {
  tracedExecutionEnvironments: MaybeArray<ExecutionEnvironment | ALL>;
  tracedQueryTypes: MaybeArray<V2_QUERY_TYPES | ALL>;
  tracedOperators: MaybeArray<OPERATORS | ALL>;
}

export interface TraceEventBase {
  type: TraceEventType;
  timestamp: Partial<TraceTimestamp>;
}

export interface TraceContext extends TraceEventBase {
  children: TraceEvent[];
  exitTimestamp?: Partial<TraceTimestamp>;
}

export const enum ExecutionEnvironment {
  api = 'api',
  sdk = 'sdk',
}

export interface TraceQueryContext extends TraceEventBase, TraceContext {
  type: TraceEventType.QueryContext;
  query: V2Query;
  result?: QueryExecutionResult;
  executionEnvironment?: ExecutionEnvironment;
}

export enum TraceMessageType {
  // general message types (0XX)
  TEXT = 0,
  TEXT_WITH_DATA = 1,
  // Conditional Expression related message types (1XX)
  QCE_OP_CALL = 101,
  QCE_OP_RETURN = 102,
  QCE_OP_TRACE = 103,
  // GraphQL related message types (2XX)
  QGQL_CACHE_READ = 201,
}

export type TraceMessageBody =
  // Conditional expression related messages
  | { type: TraceMessageType.QCE_OP_CALL; op: OPERATORS }
  | ({
      type: TraceMessageType.QCE_OP_RETURN;
      op: OPERATORS;
    } & ({ result: Literal } | { partialResult: ConditionalExpression } | { error: V2Error }))
  | { type: TraceMessageType.QCE_OP_TRACE; evaluated: boolean; args: ConditionalExpression[] }
  // GraphQL query related messages
  | {
      type: TraceMessageType.QGQL_CACHE_READ;
      keys: string[];
      values: any[];
    };

export interface TraceMessage extends TraceEventBase {
  type: TraceEventType.TraceMessage;
  message: TraceMessageBody;
}

export type TraceEvent = TraceQueryContext | TraceMessage;

export interface TraceResult {
  options: TraceOptions;
  context: TraceContext;
}

export interface TraceState extends TraceResult {
  getTraceTimestamp?: () => TraceTimestamp;
}

export const EVERY = '∀';

export const DEFAULT_TRACE_OPTIONS: TraceOptions = {
  tracedExecutionEnvironments: EVERY,
  tracedQueryTypes: EVERY,
  tracedOperators: EVERY,
};
